<template lang="pug">
#careimage-list(v-cloak)
  .box
    .box-header
      .note
        span ご家族に公開する介護記録をPDFファイルで登録してください。登録後に非公開を公開中に変更してください。
        br
        span 手書き介護記録の場合はスキャンしてPDFファイルにしてください。記録システムの場合はPDF保存してご登録ください。
      b-tabs(v-model="tabIndex")
        b-tab.tab(title="家族登録済み" active)
          CareimageTable(
            ref="careimageExistFamilyTable"
            @switch="changePublicStatus"
            @delete="deleteCareimage"
            @goto-multiple-register="gotoMultipleRegister"
            @goto-register="gotoRegister"
            :existFamily="true"
            storeNameSuffix="exist")
        b-tab.tab(title="全て")
          CareimageTable(
            ref="careimageTable"
            @switch="changePublicStatus"
            @delete="deleteCareimage"
            @goto-multiple-register="gotoMultipleRegister"
            @goto-register="gotoRegister"
            storeNameSuffix="all")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")
</template>

<script lang="ts">
import { Component, Inject, Ref, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore } from "@/store/store";
import { tabIndexesStore } from "@/store/store";

import { PcUrl } from "@/commons/constants/PcUrl";
import CareimageStatusModel from "@/commons/models/CareimageStatus";
import CareimagesService from "@/commons/services/Careimages";
import CareimageStatusesService from "@/commons/services/CareimageStatuses";
import CareimageModel from "@/commons/models/Careimage";

import CareimageTable from "@/pc/containers/careimages/CareimageTable.vue";
import OkModal from "@/pc/basics/OkModal.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * 介護記録一覧画面コンポーネント
 */
@Component({
  components: {
    CareimageTable,
    OkModal
  }
})
export default class CareimageList extends Vue {
  /** 介護記録サービス */
  @Inject("CareimagesService") careimagesService!: CareimagesService;
  /** 介護記録公開状態サービス */
  @Inject("CareimageStatusesService") careimageStatusesService!: CareimageStatusesService;

  @Ref() careimageExistFamilyTable!: CareimageTable;
  @Ref() careimageTable!: CareimageTable;

  /** 現在表示しているタブ番号 */
  tabIndex: number = 0;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /**
   * ### vue lifecycle mounted
   *
   * - タブ番号を復元後、初期値に戻す
   * - 画面読込みは非同期で行われるので、完了を待ってからタブ番号を復元する
   */
  public mounted(): void {
    this.$nextTick(() => {
      this.tabIndex = tabIndexesStore.careimageTabIndex;
      tabIndexesStore.setCareimageTabIndex({ index: 0 });
    });

    return;
  }

  /**
   * 削除確認モーダルの「削除する」ボタン押下イベント
   * モーダルで表示中の介護記録を削除する
   */
  public async deleteCareimage(careimage: CareimageModel): Promise<void> {
    await this.careimagesService.delete(userStore, careimage.residentId, careimage.datetime);

    // 成功をモーダルで通知する
    this.showDeleteSuccessModal = true;

    // 公開/非公開を変更すると全タブに影響があるため、各タブを更新する
    // 画面遷移して、タブのインスタンスが消えている場合があるため、タブが存在する場合のみrefreshする
    const careimageTable: any = this.$refs.careimageTable;
    if (careimageTable) {
      careimageTable.refresh();
    }

    const careimageExistFamilyTable: any = this.$refs.careimageExistFamilyTable;
    if (careimageExistFamilyTable) {
      careimageExistFamilyTable.refresh();
    }

    return;
  }

  /**
   * 公開・非公開確認ダイアログのボタン押下イベント
   * 公開中・非公開切り替える
   */
  public async changePublicStatus(careimage: CareimageStatusModel): Promise<void> {
    await this.updateCareimage(careimage.datetime, !careimage.isPublic);

    // 公開/非公開を変更すると全タブに影響があるため、各タブを更新する
    // 画面遷移して、タブのインスタンスが消えている場合があるため、タブが存在する場合のみrefreshする
    const careimageTable: any = this.$refs.careimageTable;
    if (careimageTable) {
      careimageTable.refresh();
    }

    const careimageExistFamilyTable: any = this.$refs.careimageExistFamilyTable;
    if (careimageExistFamilyTable) {
      careimageExistFamilyTable.refresh();
    }

    return;
  }

  /**
   * 介護記録公開情報更新
   *
   * @param date 更新対象の日付
   * @param isPublic 更新する値の公開・非公開フラグ
   */
  public async updateCareimage(datetime: number, isPublic: boolean): Promise<void> {
    // 全利用者の介護記録公開情報を更新する
    const params: any = {
      datetime: datetime,
      isPublic: isPublic,
      writtenBy: userStore.loginUser.name,
      writerId: userStore.loginUser.userId
    };

    await this.careimageStatusesService.patch(userStore, datetime, params);

    return;
  }

  /**
   * 介護記録登録画面へ遷移する
   */
  public gotoRegister(params: any): void {
    tabIndexesStore.setCareimageTabIndex({ index: this.tabIndex });
    this.$router.push({ name: PcUrl.CareimageRegister, params: params });

    return;
  }

  /**
   * 介護記録一括登録画面へ遷移する
   */
  public gotoMultipleRegister(params: any): void {
    tabIndexesStore.setCareimageTabIndex({ index: this.tabIndex });
    this.$router.push({ name: PcUrl.CareimageMultipleRegister, params: params });

    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      this.careimageExistFamilyTable.setFilterSetting();
      this.careimageTable.setFilterSetting();
    }
    next();
  }
}
</script>

<style lang="scss" scoped>
#careimage-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;
  .note {
    margin: 0 0 8px 0;
  }
}
</style>
